/**
 * Unified Status Utility - Single Source of Truth for Block Status
 * Consolidates all status logic to prevent inconsistencies across UI components
 */

import { STATUS, STATUS_UI, getStatusUI, getWorstStatus } from './statusConstants';

// Feature flag for gradual rollout
const USE_CANONICAL_STATUS = process.env.REACT_APP_USE_NEW_STATUS === 'true';

/**
 * Parse compact heartbeat event format
 * @param {Object} compactData - Compact heartbeat data with shortened keys
 * @returns {Object} - Normalized heartbeat data
 */
export const parseHeartbeatEvent = (compactData) => {
  if (!compactData || typeof compactData !== 'object') {
    return null;
  }

  // Support both compact and legacy formats
  const timestamp = compactData.ts || compactData.timestamp || Date.now();
  
  // Validate and fix timestamp formatting
  let validTimestamp;
  try {
    if (typeof timestamp === 'number') {
      validTimestamp = timestamp;
    } else if (typeof timestamp === 'string') {
      // Handle malformed timestamps like "+057500-10-24T03:58:54.000Z"
      const parsed = new Date(timestamp).getTime();
      validTimestamp = isNaN(parsed) ? Date.now() : parsed;
    } else {
      validTimestamp = Date.now();
    }
  } catch (error) {
    validTimestamp = Date.now();
  }
  
  const hasWeatherData = !!(compactData.uv !== undefined || compactData.w || compactData.weather);
  
  return {
    // Core device status
    mode: compactData.m || compactData.mode,
    active: compactData.a === 1 || compactData.active === true,
    locked: compactData.locked || false,
    
    // Battery information (standardized)
    battery_soc: compactData.b || compactData.battery_soc || compactData.battery,
    battery_charging: compactData.chg === 1 || compactData.battery_charging || compactData.charging,
    
    // Environmental sensors (standardized)
    temperature_f: compactData.t || compactData.temperature_f || compactData.temperature,
    humidity: compactData.h || compactData.humidity,
    
    // Network connectivity
    cellular_connected: compactData.cellular_connected || false,
    cloud_connected: compactData.cloud_connected || false,
    
    // Hardware health (unified - remove deprecated overlapping fields)
    // Note: Removed inTempBand, motor_healthy, proximity_sensor_ok, sensors_healthy, temp_sensor_ok
    // These are now handled by the unified HardwareHealthTracker component
    
    // Weather data (fixed timestamp handling)
    weather_conditions: compactData.w || compactData.weather_conditions || compactData.weather || "Unknown",
    uv_index: compactData.uv || compactData.uv_index,
    weather_data: {
      uv_index: compactData.uv || compactData.uv_index,
      temperature_f: compactData.t || compactData.temperature_f || compactData.temperature,
      humidity: compactData.h || compactData.humidity,
      conditions: compactData.w || compactData.weather_conditions || compactData.weather || "Unknown"
    },
    weather_valid: hasWeatherData,
    weather_timestamp: new Date(validTimestamp).toISOString(), // Properly formatted ISO timestamp
    
    // System info
    timestamp: validTimestamp,
    uptime: compactData.up || compactData.uptime
  };
};

/**
 * Parse error event format: "COMPONENT|TYPE|MESSAGE"
 * @param {string} errorData - Error event data
 * @returns {Object} - Parsed error object
 */
export const parseErrorEvent = (errorData) => {
  if (!errorData || typeof errorData !== 'string') {
    return null;
  }

  const parts = errorData.split('|');
  if (parts.length !== 3) {
    // Legacy format or malformed - return as-is
    return {
      component: 'UNKNOWN',
      type: 'UNKNOWN',
      message: errorData
    };
  }

  const [component, type, message] = parts;
  return {
    component: component.trim(),
    type: type.trim(),
    message: message.trim(),
    // Legacy compatibility
    description: message.trim()
  };
};

/**
 * Parse warning event format: "COMPONENT|TYPE|MESSAGE"
 * @param {string} warningData - Warning event data
 * @returns {Object} - Parsed warning object
 */
export const parseWarningEvent = (warningData) => {
  if (!warningData || typeof warningData !== 'string') {
    return null;
  }

  const parts = warningData.split('|');
  if (parts.length !== 3) {
    // Legacy format or malformed - return as-is
    return {
      component: 'UNKNOWN',
      type: 'UNKNOWN', 
      message: warningData
    };
  }

  const [component, type, message] = parts;
  return {
    component: component.trim(),
    type: type.trim(),
    message: message.trim(),
    // Legacy compatibility
    description: message.trim()
  };
};

/**
 * Parse diagnostics snapshot format
 * @param {Object} diagnosticsData - Full diagnostics JSON
 * @returns {Object} - Normalized diagnostics data
 */
export const parseDiagnosticsEvent = (diagnosticsData) => {
  if (!diagnosticsData || typeof diagnosticsData !== 'object') {
    return null;
  }

  return {
    timestamp: diagnosticsData.timestamp,
    device: diagnosticsData.device || {},
    battery: diagnosticsData.battery || {},
    sensors: diagnosticsData.sensors || {},
    weather: diagnosticsData.weather || {},
    errors: diagnosticsData.errors || [],
    warnings: diagnosticsData.warnings || [],
    memory: diagnosticsData.memory || {}
  };
};

/**
 * Normalize event data from various formats
 * @param {Object} eventData - Raw event data
 * @param {string} eventType - Type of event (heartbeat, error, warning, diagnostics)
 * @returns {Object} - Normalized event data
 */
export const normalizeEventData = (eventData, eventType) => {
  switch (eventType) {
    case 'heartbeat':
      return parseHeartbeatEvent(eventData);
    case 'error':
      return parseErrorEvent(eventData);
    case 'warning':
      return parseWarningEvent(eventData);
    case 'diagnostics':
      return parseDiagnosticsEvent(eventData);
    default:
      return eventData;
  }
};

/**
 * Get the overall block health status from all available data sources
 * @param {Object} block - Block data from API
 * @param {Array} errors - Error array for the block
 * @param {Array} warnings - Warning array for the block
 * @returns {Object} - { status, reason, color, icon }
 */
export const getUnifiedBlockStatus = (block, errors = [], warnings = []) => {
  // NEW: Use canonical status if available and feature flag enabled
  if (USE_CANONICAL_STATUS && block?.status) {
    const ui = getStatusUI(block.status);
    return {
      status: block.status,
      reason: block.statusReason || ui.label,
      color: ui.color,
      icon: ui.icon,
      priority: ui.priority
    };
  }

  // LEGACY: Keep existing logic for transition period
  // Check if block is offline (no recent heartbeat - 3 hours as per user requirement)
  const lastHeartbeat = block?.lastHeartbeat || block?.last_heartbeat;
  const now = new Date();
  const heartbeatDate = lastHeartbeat ? new Date(lastHeartbeat) : null;

  // Time thresholds (in ms) - UPDATED to match new requirements
  const ONE_HOUR = 60 * 60 * 1000;      // 1 hour
  const THREE_HOURS = 3 * ONE_HOUR;     // 3 hours

  const ageMs = heartbeatDate ? (now - heartbeatDate) : Infinity;

  const withinOneHour = ageMs <= ONE_HOUR;

  const isOffline = ageMs > THREE_HOURS; // >3h ⇒ critical offline
 
  // Check for errors and warnings (use activeErrors/activeWarnings if available)
  const activeErrors = block?.activeErrors || errors || [];
  const activeWarnings = block?.activeWarnings || warnings || [];
  const hasErrors = activeErrors.length > 0;
  const hasWarnings = activeWarnings.length > 0;
 
  // Priority 1: CRITICAL if error OR offline
  if (hasErrors || isOffline) {
    let reason;
    if (hasErrors && isOffline) {
      reason = `${activeErrors.length} error${activeErrors.length > 1 ? 's' : ''} + offline`;
    } else if (hasErrors) {
      reason = `${activeErrors.length} error${activeErrors.length > 1 ? 's' : ''}`;
    } else {
      reason = heartbeatDate
        ? `Device offline for ${getTimeAgo(heartbeatDate)}`
        : 'No heartbeat received';
    }
    
    return {
      status: STATUS.CRITICAL,
      reason,
      color: STATUS_UI[STATUS.CRITICAL].color,
      icon: STATUS_UI[STATUS.CRITICAL].icon,
      priority: STATUS_UI[STATUS.CRITICAL].priority
    };
  }
 
  // Priority 2: WARNING if only warnings (and online)
  if (hasWarnings || !withinOneHour) {
    // Determine reason
    let reason;
    if (hasWarnings) {
      reason = `${activeWarnings.length} warning${activeWarnings.length > 1 ? 's' : ''}`;
    } else {
      // No warnings but heartbeat is stale between 1-2h
      reason = heartbeatDate ? `Last check ${getTimeAgo(heartbeatDate)}` : 'Heartbeat >1h';
    }
    return {
      status: STATUS.WARNING,
      reason,
      color: STATUS_UI[STATUS.WARNING].color,
      icon: STATUS_UI[STATUS.WARNING].icon,
      priority: STATUS_UI[STATUS.WARNING].priority
    };
  }
 
  // Priority 3: HEALTHY if heartbeat within 1h and no errors/warnings
  return {
    status: STATUS.HEALTHY,
    reason: 'All systems operational',
    color: STATUS_UI[STATUS.HEALTHY].color,
    icon: STATUS_UI[STATUS.HEALTHY].icon,
    priority: STATUS_UI[STATUS.HEALTHY].priority
  };
};

/**
 * CENTRAL STATUS NORMALIZATION
 * 
 * This function ensures that ALL components use the same status calculation logic
 * and eliminates inconsistencies across the dashboard. It's the single source of truth.
 * 
 * Call this function from ALL components instead of individual status calculations.
 */
export const getNormalizedBlockStatus = (block, errors = [], warnings = []) => {
  // Validate inputs
  if (!block) {
    return {
      status: STATUS.CRITICAL,
      reason: 'No block data available',
      color: STATUS_UI[STATUS.CRITICAL].color,
      icon: STATUS_UI[STATUS.CRITICAL].icon,
      priority: STATUS_UI[STATUS.CRITICAL].priority
    };
  }

  // PRIORITY: Use canonical status from server if available
  if (block.hasCanonicalStatus && block.status && block.statusReason) {
    const canonicalStatus = block.status.toLowerCase();
    const statusKey = canonicalStatus === 'healthy' ? STATUS.HEALTHY :
                     canonicalStatus === 'warning' ? STATUS.WARNING :
                     canonicalStatus === 'critical' ? STATUS.CRITICAL : STATUS.UNKNOWN;
    
    const ui = STATUS_UI[statusKey];
    return {
      status: statusKey,
      reason: block.statusReason,
      color: ui.color,
      icon: ui.icon,
      priority: ui.priority
    };
  }

  // Use activeErrors/activeWarnings if available, fallback to errors/warnings
  const activeErrors = block?.activeErrors || errors || [];
  const activeWarnings = block?.activeWarnings || warnings || [];

  // Handle offline status consistently - if is_online is explicitly false, it's critical
  const isExplicitlyOffline = block.is_online === false || block.cloud_connected === false;
  
  // Calculate heartbeat age consistently
  const lastHeartbeat = block?.lastHeartbeat || block?.last_heartbeat || block?.lastChecked;
  const now = new Date();
  const heartbeatDate = lastHeartbeat ? new Date(lastHeartbeat) : null;
  const ageMs = heartbeatDate ? (now - heartbeatDate) : Infinity;
  
  // Time thresholds (in ms) - standardized across all components
  const ONE_HOUR = 60 * 60 * 1000;      
  const TWO_HOURS = 2 * ONE_HOUR;       
  
  const withinOneHour = ageMs <= ONE_HOUR;
  const isHeartbeatStale = ageMs > TWO_HOURS;

  // PRIORITY 1: CRITICAL if explicitly offline OR errors OR heartbeat >2h
  if (isExplicitlyOffline || activeErrors.length > 0 || isHeartbeatStale) {
    let reason;
    
    if (isExplicitlyOffline) {
      reason = 'Device Offline';
    } else if (activeErrors.length > 0 && isHeartbeatStale) {
      reason = `${activeErrors.length} error${activeErrors.length > 1 ? 's' : ''} + offline`;
    } else if (activeErrors.length > 0) {
      reason = `${activeErrors.length} error${activeErrors.length > 1 ? 's' : ''}`;
    } else {
      reason = heartbeatDate 
        ? `Device offline for ${getTimeAgo(heartbeatDate)}`
        : 'No heartbeat received';
    }
    
    return {
      status: STATUS.CRITICAL,
      reason,
      color: STATUS_UI[STATUS.CRITICAL].color,
      icon: STATUS_UI[STATUS.CRITICAL].icon,
      priority: STATUS_UI[STATUS.CRITICAL].priority
    };
  }

  // PRIORITY 2: WARNING if warnings OR heartbeat 1-2h stale
  if (activeWarnings.length > 0 || !withinOneHour) {
    let reason;
    
    if (activeWarnings.length > 0) {
      reason = `${activeWarnings.length} warning${activeWarnings.length > 1 ? 's' : ''}`;
    } else {
      reason = heartbeatDate 
        ? `Last check ${getTimeAgo(heartbeatDate)}` 
        : 'Heartbeat >1h';
    }
    
    return {
      status: STATUS.WARNING,
      reason,
      color: STATUS_UI[STATUS.WARNING].color,
      icon: STATUS_UI[STATUS.WARNING].icon,
      priority: STATUS_UI[STATUS.WARNING].priority
    };
  }

  // PRIORITY 3: HEALTHY - all good
  return {
    status: STATUS.HEALTHY,
    reason: 'All systems operational',
    color: STATUS_UI[STATUS.HEALTHY].color,
    icon: STATUS_UI[STATUS.HEALTHY].icon,
    priority: STATUS_UI[STATUS.HEALTHY].priority
  };
};

/**
 * Get location status based on worst block status at that location
 * @param {Array} blocks - Array of blocks at the location
 * @param {Object} errors - Errors object keyed by serial
 * @param {Object} warnings - Warnings object keyed by serial
 * @returns {Object} - Status object
 */
export const getLocationStatus = (blocks, errors = {}, warnings = {}) => {
  if (!blocks || blocks.length === 0) {
    return {
      status: STATUS.CRITICAL,
      reason: 'No blocks at location',
      color: STATUS_UI[STATUS.CRITICAL].color,
      icon: STATUS_UI[STATUS.CRITICAL].icon
    };
  }

  // NEW: Use canonical status if available
  if (USE_CANONICAL_STATUS && blocks.every(block => block.status)) {
    const statuses = blocks.map(block => block.status);
    const worstStatus = getWorstStatus(statuses);
    const ui = getStatusUI(worstStatus);
    
    const criticalCount = statuses.filter(s => s === STATUS.CRITICAL).length;
    const warningCount = statuses.filter(s => s === STATUS.WARNING).length;
    
    let reason = 'All systems operational';
    if (criticalCount > 0) {
      reason = `${criticalCount} of ${blocks.length} blocks critical`;
    } else if (warningCount > 0) {
      reason = `${warningCount} of ${blocks.length} blocks have warnings`;
    }
    
    return {
      status: worstStatus,
      reason,
      color: ui.color,
      icon: ui.icon,
      priority: ui.priority
    };
  }

  // LEGACY: Keep existing logic
  let worstStatus = { priority: 0 };
  
  blocks.forEach(block => {
    const blockErrors = errors[block.serial] || errors[block.id] || [];
    const blockWarnings = warnings[block.serial] || warnings[block.id] || [];
    const status = getUnifiedBlockStatus(block, blockErrors, blockWarnings);
    
    if (status.priority > worstStatus.priority) {
      worstStatus = status;
    }
  });

  return worstStatus;
};

/**
 * Get placeholder status for missing data
 * @param {string} dataType - Type of missing data (battery, temperature, humidity, etc.)
 * @returns {Object} - Status object with placeholder info
 */
export const getMissingDataStatus = (dataType) => {
  const placeholders = {
    battery: {
      value: '--',
      reason: 'Battery data not available',
      color: STATUS_UI[STATUS.CRITICAL].color
    },
    temperature: {
      value: '--°F',
      reason: 'Temperature data not available',
      color: STATUS_UI[STATUS.CRITICAL].color
    },
    humidity: {
      value: '--%',
      reason: 'Humidity data not available',
      color: STATUS_UI[STATUS.CRITICAL].color
    },
    fillLevel: {
      value: '--ml',
      reason: 'Fill level data not available',
      color: STATUS_UI[STATUS.CRITICAL].color
    }
  };

  return placeholders[dataType] || {
    value: '--',
    reason: 'Data not available',
    color: STATUS_UI[STATUS.CRITICAL].color
  };
};

/**
 * Get time ago string for status tooltips
 * @param {Date} date - Date to compare
 * @returns {string} - Human readable time ago
 */
export const getTimeAgo = (date) => {
  const now = new Date();
  const diffMs = now - date;
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

/**
 * Legacy recommendation system for error/warning types
 * @param {string} type - Error or warning type
 * @returns {string} - Recommendation text
 */
export const getStatusRecommendation = (type) => {
  const recommendations = {
    'BATTERY_LOW_75': 'Schedule battery replacement within 1 week',
    'BATTERY_LOW_50': 'Consider battery replacement soon - performance may be impacted',
    'BATTERY_CRITICAL': 'Replace battery immediately - device will shut down soon',
    'FILL_LEVEL_LOW_20': 'Refill sunscreen bag to ensure continuous service',
    'FILL_LEVEL_LOW_10': 'Refill sunscreen bag immediately - very low supply',
    'FILL_LEVEL_CRITICAL_5': 'URGENT: Refill sunscreen bag now - critically low',
    'FILL_EMPTY': 'Refill sunscreen bag immediately - no dispensing possible',
    'TEMP_SENSOR_DOWN': 'Check temperature sensor connections and restart device',
    'TEMP_EXTREME': 'Move device to cooler location or provide additional shade',
    'HIGH_TEMPERATURE': 'Ensure proper ventilation and shade coverage',
    'VP7200_OFFLINE': 'Check payment processor connections - QR codes still functional',
    'PROXIMITY_FAILED': 'Replace proximity sensor - no hand detection possible',
    'PROXIMITY_DRIFT': 'Recalibrate proximity sensor baseline',
    'DISPLAY_FAILED': 'Service display unit - visual feedback unavailable',
    'MOTOR_JAMMED': 'Clear motor obstruction or replace motor assembly',
    'MOTOR_SLOW_RESPONSE': 'Check motor connections and lubrication',
    'CLOUD_OFFLINE': 'Restore internet connection - device may switch to offline mode',
    'CLOUD_INTERMITTENT': 'Check internet connection stability',
    'SYSTEM_UNSTABLE': 'Restart device immediately - critical system issues',
    'MEMORY_LOW': 'Monitor device performance - may need restart',
    'MEMORY_CRITICAL': 'Restart device now - system may crash',
    'SENSOR_MULTIPLE_FAIL': 'Service device immediately - multiple critical failures'
  };
  
  return recommendations[type] || 'Monitor situation and contact support if needed';
};

export default {
  getUnifiedBlockStatus,
  getLocationStatus,
  getMissingDataStatus,
  getTimeAgo,
  getStatusRecommendation,
  parseHeartbeatEvent,
  parseErrorEvent,
  parseWarningEvent,
  parseDiagnosticsEvent,
  normalizeEventData,
  getNormalizedBlockStatus
};
